/**
 * Sprint T-17.1 · Notification Center · WebhookEndpointsTab.
 * CRUD registrados · signing_secret one-time reveal.
 * §4.6
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Webhook, ExternalLink, AlertTriangle } from "lucide-react";
import { WidgetCard, WidgetGrid, EmptyState, LoadingState, MetricCard, CitationBadge } from "../advisor";
import { useWebhookEndpoints, useCreateWebhookEndpoint, useDeleteWebhookEndpoint } from "../../hooks/useNotifications";
import { RelativeTime } from "../automationCenter/shared/Formatters";
import { DeleteEndpointDialog, ModalShell, SigningSecretDialog } from "./shared/Dialogs";

function urlIsProbablySafeHttps(url) {
  try {
    const u = new URL(url);
    if (u.protocol !== "https:") return { ok: false, reason: "Debe empezar por https://" };
    const host = u.hostname.toLowerCase();
    if (host === "localhost" || host === "0.0.0.0") return { ok: false, reason: "No se permite localhost" };
    if (/^127\./.test(host) || /^10\./.test(host) || /^192\.168\./.test(host))
      return { ok: false, reason: "No se permiten IPs privadas" };
    return { ok: true };
  } catch {
    return { ok: false, reason: "URL inválida" };
  }
}

function CreateEndpointDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("https://");
  const [tenantId, setTenantId] = useState("");
  const [description, setDescription] = useState("");
  const create = useCreateWebhookEndpoint();
  const validation = urlIsProbablySafeHttps(url);

  const submit = async () => {
    if (!name.trim()) return toast.error("Nombre obligatorio");
    if (!validation.ok) return toast.error(validation.reason);
    try {
      const res = await create.mutateAsync({
        name: name.trim(),
        url: url.trim(),
        tenant_id: tenantId.trim() || null,
        description: description.trim() || null,
        active: true,
      });
      onCreated(res.endpoint);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el endpoint");
    }
  };

  return (
    <ModalShell
      title="Nuevo webhook endpoint"
      onClose={onClose}
      testId="notif-create-endpoint-dialog"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="notif-create-endpoint-cancel"
            onClick={onClose}
            className="rounded border border-[var(--ag-border)] px-3 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="notif-create-endpoint-submit"
            onClick={submit}
            disabled={create.isPending || !name.trim() || !validation.ok}
            className="inline-flex items-center gap-1 rounded bg-[var(--ag-gold)] px-3 py-1 text-xs font-medium text-black hover:opacity-90 disabled:opacity-50"
          >
            <Plus className="h-3 w-3" /> {create.isPending ? "Creando…" : "Crear endpoint"}
          </button>
        </div>
      }
    >
      <div className="space-y-3 text-[11px]">
        <div>
          <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">Nombre</label>
          <input
            type="text"
            data-testid="notif-create-endpoint-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="stripe-webhook · zapier-integration · …"
            className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 font-mono text-xs"
          />
        </div>
        <div>
          <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">URL (HTTPS obligatorio · anti-SSRF activo)</label>
          <input
            type="url"
            data-testid="notif-create-endpoint-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://your-domain.com/hook"
            className={`w-full rounded border bg-[var(--ag-surface)] px-2 py-1.5 font-mono text-xs ${
              validation.ok ? "border-[var(--ag-border)]" : "border-red-500/50"
            }`}
          />
          {!validation.ok && (
            <div data-testid="notif-create-endpoint-url-error" className="mt-1 flex items-center gap-1 text-[10px] text-red-500">
              <AlertTriangle className="h-3 w-3" /> {validation.reason}
            </div>
          )}
        </div>
        <div>
          <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">tenant_id (opcional · vacío = global)</label>
          <input
            type="text"
            data-testid="notif-create-endpoint-tenant"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder="org UUID · dejar vacío para scope global"
            className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 font-mono text-xs"
          />
        </div>
        <div>
          <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">Descripción (opcional)</label>
          <input
            type="text"
            data-testid="notif-create-endpoint-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Para qué usarás este endpoint"
            className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-xs"
          />
        </div>
      </div>
    </ModalShell>
  );
}

function EndpointCard({ endpoint, onDelete, index }) {
  return (
    <div
      data-testid={`notif-endpoint-row-${index}`}
      className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Webhook className="h-3.5 w-3.5 text-fuchsia-400" />
            <span className="font-mono text-xs font-semibold truncate">{endpoint.name}</span>
            {endpoint.active ? (
              <span className="rounded border border-emerald-500/30 bg-emerald-500/10 px-1 py-0.5 text-[9px] uppercase tracking-wide text-emerald-500">
                active
              </span>
            ) : (
              <span className="rounded border border-slate-500/30 bg-slate-500/10 px-1 py-0.5 text-[9px] uppercase tracking-wide text-slate-400">
                inactive
              </span>
            )}
          </div>
          <a
            href={endpoint.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 truncate text-[10px] text-sky-400 hover:underline"
            title={endpoint.url}
          >
            <ExternalLink className="h-2.5 w-2.5" />
            <span className="truncate">{endpoint.url}</span>
          </a>
          <div className="mt-1 text-[10px] text-[var(--ag-text-dim)]">
            tenant: <span className="font-mono">{endpoint.tenant_id || "global"}</span>
          </div>
          {endpoint.description && (
            <div className="mt-1 line-clamp-2 text-[10px] text-[var(--ag-text-dim)]">
              {endpoint.description}
            </div>
          )}
          <div className="mt-1 grid grid-cols-2 gap-2 text-[10px] text-[var(--ag-text-dim)]">
            <div>secret: <span className="font-mono">{endpoint.signing_secret}</span></div>
            <div>
              último uso:{" "}
              <span className="font-mono">
                {endpoint.last_used_at ? <RelativeTime iso={endpoint.last_used_at} /> : "—"}
              </span>
            </div>
          </div>
        </div>
        <button
          type="button"
          data-testid={`notif-endpoint-delete-${index}`}
          onClick={() => onDelete(endpoint)}
          className="shrink-0 rounded border border-red-500/40 bg-red-500/10 p-1 text-red-500 hover:bg-red-500/20"
          aria-label="Eliminar endpoint"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

export default function WebhookEndpointsTab() {
  const q = useWebhookEndpoints({ active_only: false });
  const del = useDeleteWebhookEndpoint();
  const [openCreate, setOpenCreate] = useState(false);
  const [showSecret, setShowSecret] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);

  const items = q.data?.items || [];
  const total = q.data?.count || 0;

  const doDelete = async () => {
    try {
      await del.mutateAsync(deleteFor.id);
      toast.success("Endpoint eliminado");
      setDeleteFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <section className="space-y-4" data-testid="notif-endpoints-tab">
      <WidgetGrid cols="md:grid-cols-3">
        <MetricCard label="Total endpoints" value={total} testId="notif-endpoints-total" hint="registrados" />
        <MetricCard
          label="Activos"
          value={items.filter((e) => e.active).length}
          testId="notif-endpoints-active"
          hint="pueden recibir"
        />
        <MetricCard
          label="Global scope"
          value={items.filter((e) => !e.tenant_id).length}
          testId="notif-endpoints-global"
          hint="sin tenant"
        />
      </WidgetGrid>

      <WidgetCard
        title="Webhook Endpoints"
        description="Solo URLs pre-registradas HTTPS · anti-SSRF · HMAC-SHA256"
        citations={["/api/notifications/webhook-endpoints"]}
        testId="notif-endpoints-list"
        actions={
          <button
            type="button"
            data-testid="notif-create-endpoint-btn"
            onClick={() => setOpenCreate(true)}
            className="inline-flex items-center gap-1 rounded bg-[var(--ag-gold)] px-2 py-1 text-[11px] font-medium text-black hover:opacity-90"
          >
            <Plus className="h-3 w-3" /> Nuevo endpoint
          </button>
        }
      >
        {q.isLoading && <LoadingState />}
        {q.error && <EmptyState message="No se pudieron cargar los endpoints" testId="notif-endpoints-error" />}
        {q.data && items.length === 0 && (
          <EmptyState
            title="Sin endpoints registrados"
            message="Crea el primero para enviar webhooks salientes. Solo URLs HTTPS públicas son admitidas (§17.7)."
            testId="notif-endpoints-empty"
          />
        )}
        {items.length > 0 && (
          <WidgetGrid cols="md:grid-cols-2 lg:grid-cols-2">
            {items.map((e, i) => (
              <EndpointCard key={e.id} endpoint={e} index={i} onDelete={setDeleteFor} />
            ))}
          </WidgetGrid>
        )}
      </WidgetCard>

      {openCreate && (
        <CreateEndpointDialog
          onClose={() => setOpenCreate(false)}
          onCreated={(ep) => setShowSecret(ep)}
        />
      )}
      {showSecret && (
        <SigningSecretDialog endpoint={showSecret} onClose={() => setShowSecret(null)} />
      )}
      {deleteFor && (
        <DeleteEndpointDialog
          endpoint={deleteFor}
          onClose={() => setDeleteFor(null)}
          onConfirm={doDelete}
          pending={del.isPending}
        />
      )}

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["§17.7 · pre-registered URLs only · HMAC signature per attempt"]} />
      </footer>
    </section>
  );
}
